"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Info } from "lucide-react";
const pricingData = [{
  id: "sedan",
  vehicleClass: "Luxury Sedan",
  passengers: "1-3",
  hourlyRate: "$85 / hr",
  flatRate: "From $150",
  minHours: "2 hrs"
}, {
  id: "suv",
  vehicleClass: "Premium SUV",
  passengers: "1-6",
  hourlyRate: "$110 / hr",
  flatRate: "From $195",
  minHours: "3 hrs"
}, {
  id: "limo",
  vehicleClass: "Stretch Limousine",
  passengers: "1-8",
  hourlyRate: "$150 / hr",
  flatRate: "From $295",
  minHours: "4 hrs"
}, {
  id: "sprinter",
  vehicleClass: "Executive Sprinter",
  passengers: "1-14",
  hourlyRate: "$175 / hr",
  flatRate: "From $350",
  minHours: "4 hrs"
}, {
  id: "partybus",
  vehicleClass: "Luxury Party Bus",
  passengers: "1-24",
  hourlyRate: "$250 / hr",
  flatRate: "Custom Quote",
  minHours: "5 hrs"
}];
export default function PricingOptions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="pricing-options" className="bg-background section-padding relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div ref={ref} className={`grid grid-cols-12 gap-6 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="col-span-12 text-center mb-10 lg:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
              Transparent Premium Pricing
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Experience uncompromising luxury with our straightforward, competitive rates. Choose between flexible hourly service or convenient flat-rate transfers.
            </p>
          </div>

          {}
          <div className="col-span-12 lg:col-span-10 lg:col-start-2">
            <div className="bg-background rounded-xl overflow-hidden shadow-[0_4px_12px_hsla(0,0%,0%,0.08)] border border-border/50">
              <Table className="w-full border-collapse">
                <TableHeader className="bg-primary [&_tr]:border-0">
                  <TableRow className="hover:bg-primary border-0">
                    <TableHead className="text-white font-display text-lg py-5 px-6 w-[30%]">
                      Vehicle Class
                    </TableHead>
                    <TableHead className="text-white font-display text-lg py-5 px-6 text-center">
                      Capacity
                    </TableHead>
                    <TableHead className="text-white font-display text-lg py-5 px-6 text-center">
                      Hourly Rate
                    </TableHead>
                    <TableHead className="text-white font-display text-lg py-5 px-6 text-right">
                      Flat Rate (Transfer)
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="[&_tr:last-child]:border-0">
                  {pricingData.map((row, i) => <TableRow key={row.id} data-index={i} className={`border-0 transition-colors hover:bg-muted/50 ${i % 2 === 0 ? "bg-background" : "bg-muted/20"}`}>
                      <TableCell className="py-5 px-6">
                        <div className="flex flex-col">
                          <span className="font-semibold text-foreground text-base">
                            {row.vehicleClass}
                          </span>
                          <span className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <Info className="w-3 h-3" /> Min. {row.minHours}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="py-5 px-6 text-center text-muted-foreground font-medium">
                        {row.passengers} Pax
                      </TableCell>
                      <TableCell className="py-5 px-6 text-center font-semibold text-foreground">
                        {row.hourlyRate}
                      </TableCell>
                      <TableCell className="py-5 px-6 text-right font-semibold text-foreground">
                        {row.flatRate}
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </div>

            {}
            <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-6 bg-muted/10 p-6 rounded-lg border border-border/50">
              <p className="text-sm text-muted-foreground max-w-xl">
                * Rates are subject to change based on seasonality, availability, and specific routing. Gratuity, tolls, and parking are not included in base rates.
              </p>
              <div className="shrink-0">
                <Button asChild className="btn-secondary rounded-md px-8 py-4 h-auto text-base whitespace-nowrap">
                  <Link to="/ContactPage">Request Custom Event Quote</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}